/**
 * PublisherApiPage — partner API key console (FS-142 / DASH).
 *
 * Single-page admin surface that aggregates four sub-widgets:
 *
 *   - Key generator            (affiliate id + scopes + label → POST /keys)
 *   - One-time reveal panel    (raw_key shown ONCE after a successful POST;
 *                               cleared on next generate)
 *   - Keys table               (GET /keys/:affiliate_id; mask + last_used)
 *   - Scope editor             (per-row checkboxes that currently only
 *                               toggle locally — the API has no PATCH yet,
 *                               so scopes are read-only in practice; the UI
 *                               still communicates the available scopes)
 *
 * Follows the same light-theme token set as AdvertiserPortalPage so the
 * visual identity stays consistent across all DASH feature pages. Brand
 * rules: emerald→sky gradient is reserved for the headline.
 *
 * No @tanstack/react-query — three widgets hydrate in parallel via
 * `Promise.allSettled`, mirroring advertiser-portal's pattern.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
	AVAILABLE_SCOPES,
	type PublisherApiKeyCreatedResponse,
	type PublisherApiKeyMasked,
	type PublisherScope,
	createKey,
	createKeyFormSchema,
	fetchKeys,
	fetchScopes,
	formatKeyStatus,
	formatLastUsed,
	maskedKeyDisplay,
	revokeKey,
	// FS-226
	PUBLISHER_SCOPES_V2,
	type PublisherScopeV2,
	type PublisherApiKeyMaskedV2,
	type PublisherApiKeyCreatedV2Response,
	type PublisherApiEndpointDescriptor,
	type PublisherApiAccessLogEntry,
	createKeyV2,
	createKeyV2FormSchema,
	deleteKeyV2,
	fetchAccessLog,
	fetchEndpoints,
	fetchKeysV2,
	formatAccessLogTimestamp,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — reused from advertiser-portal so dashboards stay visually
// consistent without pulling a shared CSS module (out-of-lane file).
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function PublisherApiPage() {
	const [affiliateId, setAffiliateId] = useState("");
	const [rows, setRows] = useState<PublisherApiKeyMasked[]>([]);
	const [loadingRows, setLoadingRows] = useState(false);
	const [rowsError, setRowsError] = useState<string | null>(null);

	const [availableScopes, setAvailableScopes] = useState<PublisherScope[]>([
		...AVAILABLE_SCOPES,
	]);
	const [reveal, setReveal] = useState<
		PublisherApiKeyCreatedResponse | null
	>(null);

	const reloadKeys = useCallback(async (id: string) => {
		const trimmed = id.trim();
		if (!trimmed) {
			setRows([]);
			return;
		}
		setLoadingRows(true);
		setRowsError(null);
		try {
			const fresh = await fetchKeys(trimmed);
			setRows(fresh);
		} catch (err) {
			setRowsError(
				err instanceof Error ? err.message : "Unable to load keys.",
			);
		} finally {
			setLoadingRows(false);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const scopes = await fetchScopes();
				if (!cancelled && scopes.length > 0) setAvailableScopes(scopes);
			} catch {
				/* fall back to the static constant */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Breadcrumb />
			<Heading />
			<GeneratorCard
				availableScopes={availableScopes}
				defaultAffiliateId={affiliateId}
				onGenerated={async (created, submittedId) => {
					setReveal(created);
					setAffiliateId(submittedId);
					await reloadKeys(submittedId);
				}}
			/>
			<RevealPanel
				reveal={reveal}
				onDismiss={() => setReveal(null)}
			/>
			<KeysCard
				affiliateId={affiliateId}
				setAffiliateId={setAffiliateId}
				rows={rows}
				loading={loadingRows}
				error={rowsError}
				availableScopes={availableScopes}
				onReload={reloadKeys}
				onRevoke={async (id) => {
					try {
						await revokeKey(id);
						await reloadKeys(affiliateId);
					} catch (err) {
						setRowsError(
							err instanceof Error ? err.message : "Unable to revoke key.",
						);
					}
				}}
			/>
			<PublisherV2Console />
			<EndpointsCatalog />
			<DocHint />
		</main>
	);
}

export default PublisherApiPage;

// ---------------------------------------------------------------------------
// Header fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5 text-zinc-600">
						Publishers
					</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">API keys</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Publishers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Publisher API keys
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Mint partner-facing API keys for affiliates. Each key is scoped to
				campaigns, stats, and/or creatives, and the raw secret is revealed
				exactly once — copy it immediately after generation.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Generator card
// ---------------------------------------------------------------------------

interface GeneratorState {
	affiliateId: string;
	scopes: PublisherScope[];
	label: string;
	submitting: boolean;
	errors: Record<string, string>;
	serverError: string | null;
}

function GeneratorCard(props: {
	availableScopes: PublisherScope[];
	defaultAffiliateId: string;
	onGenerated: (
		res: PublisherApiKeyCreatedResponse,
		affiliateId: string,
	) => Promise<void>;
}) {
	const [state, setState] = useState<GeneratorState>({
		affiliateId: props.defaultAffiliateId,
		scopes: [],
		label: "",
		submitting: false,
		errors: {},
		serverError: null,
	});

	function toggleScope(scope: PublisherScope) {
		setState((prev) => {
			const next = prev.scopes.includes(scope)
				? prev.scopes.filter((s) => s !== scope)
				: [...prev.scopes, scope];
			return { ...prev, scopes: next, errors: { ...prev.errors, scopes: "" } };
		});
	}

	async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = createKeyFormSchema.safeParse({
			affiliateId: state.affiliateId,
			scopes: state.scopes,
			label: state.label,
		});
		if (!parsed.success) {
			setState((prev) => ({
				...prev,
				errors: parsed.errors,
				serverError: null,
			}));
			return;
		}
		setState((prev) => ({
			...prev,
			submitting: true,
			errors: {},
			serverError: null,
		}));
		try {
			const created = await createKey(parsed.data);
			await props.onGenerated(created, parsed.data.affiliate_id);
			setState((prev) => ({
				...prev,
				submitting: false,
				label: "",
				scopes: [],
			}));
		} catch (err) {
			setState((prev) => ({
				...prev,
				submitting: false,
				serverError:
					err instanceof Error ? err.message : "Unable to generate key.",
			}));
		}
	}

	return (
		<section
			aria-label="Generate a partner API key"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					Generate a new key
				</h2>
				<p className={HINT}>
					Returns the raw secret <strong>once</strong>. Save it immediately —
					the platform stores only a SHA-256 hash.
				</p>
			</header>
			<form
				onSubmit={onSubmit}
				className="flex flex-col gap-4"
				aria-label="Key generator form"
				noValidate
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="pub-gen-affiliate" className={LABEL}>
						Affiliate ID
					</label>
					<input
						id="pub-gen-affiliate"
						name="affiliateId"
						type="text"
						placeholder="aff_01"
						className={INPUT}
						value={state.affiliateId}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								affiliateId: e.currentTarget.value,
								errors: { ...prev.errors, affiliateId: "" },
							}))
						}
						aria-invalid={Boolean(state.errors.affiliateId)}
						aria-describedby={
							state.errors.affiliateId ? "pub-gen-affiliate-error" : undefined
						}
						disabled={state.submitting}
					/>
					{state.errors.affiliateId && (
						<p id="pub-gen-affiliate-error" className={ERROR_TEXT}>
							{state.errors.affiliateId}
						</p>
					)}
				</div>
				<fieldset className="flex flex-col gap-2" aria-describedby="pub-gen-scopes-hint">
					<legend className={LABEL}>Scopes</legend>
					<p id="pub-gen-scopes-hint" className={HINT}>
						Pick the API surfaces this key is allowed to call.
					</p>
					<div className="flex flex-wrap gap-3">
						{props.availableScopes.map((scope) => {
							const checked = state.scopes.includes(scope);
							return (
								<label
									key={scope}
									className="inline-flex cursor-pointer items-center gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm text-zinc-800 hover:border-emerald-300 hover:bg-emerald-50"
								>
									<input
										type="checkbox"
										name="scope"
										value={scope}
										checked={checked}
										onChange={() => toggleScope(scope)}
										className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
										disabled={state.submitting}
									/>
									<span className="font-mono text-xs">{scope}</span>
								</label>
							);
						})}
					</div>
					{state.errors.scopes && (
						<p className={ERROR_TEXT}>{state.errors.scopes}</p>
					)}
				</fieldset>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="pub-gen-label" className={LABEL}>
						Label (optional)
					</label>
					<input
						id="pub-gen-label"
						name="label"
						type="text"
						placeholder="homepage integration"
						className={INPUT}
						value={state.label}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								label: e.currentTarget.value,
								errors: { ...prev.errors, label: "" },
							}))
						}
						aria-invalid={Boolean(state.errors.label)}
						disabled={state.submitting}
					/>
					{state.errors.label && (
						<p className={ERROR_TEXT}>{state.errors.label}</p>
					)}
				</div>
				<div className="flex items-center justify-between gap-2">
					<p className={HINT}>Submits to POST /publisher-api/keys</p>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={state.submitting}
					>
						{state.submitting ? "Generating." : "Generate key"}
					</button>
				</div>
				{state.serverError && (
					<p className={ERROR_TEXT} role="alert">
						{state.serverError}
					</p>
				)}
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Reveal panel — shown once after a successful POST /keys.
// ---------------------------------------------------------------------------

function RevealPanel(props: {
	reveal: PublisherApiKeyCreatedResponse | null;
	onDismiss: () => void;
}) {
	if (!props.reveal) return null;
	const { key, raw_key } = props.reveal;
	return (
		<section
			aria-label="One-time key reveal"
			className="rounded-[8px] border border-emerald-300 bg-emerald-50 p-5"
		>
			<header className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-emerald-800">
						Copy this key now
					</h2>
					<p className="text-xs text-emerald-700">
						You will not see this secret again. Store it in your secret
						manager before dismissing.
					</p>
				</div>
				<button
					type="button"
					className={BTN_SECONDARY}
					onClick={props.onDismiss}
					aria-label="Dismiss reveal panel"
				>
					Dismiss
				</button>
			</header>
			<div className="mt-3 flex flex-col gap-2">
				<code
					className="break-all rounded-[6px] border border-emerald-400 bg-white px-3 py-2 font-mono text-sm text-emerald-900"
					data-testid="publisher-api-raw-key"
				>
					{raw_key}
				</code>
				<p className="text-xs text-emerald-700">
					Prefix <span className="font-mono">{key.key_prefix}</span>
					{" "}· Scopes <span className="font-mono">{key.scopes.join(", ") || "none"}</span>
					{" "}· Label <span className="font-mono">{key.label ?? "—"}</span>
				</p>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Keys table card
// ---------------------------------------------------------------------------

interface KeysCardProps {
	affiliateId: string;
	setAffiliateId: (id: string) => void;
	rows: PublisherApiKeyMasked[];
	loading: boolean;
	error: string | null;
	availableScopes: PublisherScope[];
	onReload: (id: string) => Promise<void>;
	onRevoke: (id: string) => Promise<void>;
}

function KeysCard(props: KeysCardProps) {
	const sorted = useMemo(
		() =>
			[...props.rows].sort(
				(a, b) =>
					Number(Boolean(a.revoked_at)) - Number(Boolean(b.revoked_at)) ||
					b.created_at - a.created_at,
			),
		[props.rows],
	);

	return (
		<section
			aria-label="Existing API keys"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1">
					<h2 className="text-lg font-semibold text-zinc-900">
						Existing keys
					</h2>
					<p className={HINT}>
						Masked list scoped to an affiliate. Last-used is updated on each
						successful partner-API call.
					</p>
				</div>
				<form
					onSubmit={async (e) => {
						e.preventDefault();
						await props.onReload(props.affiliateId);
					}}
					className="flex items-end gap-2"
				>
					<div className="flex flex-col gap-1">
						<label htmlFor="pub-keys-affiliate" className={LABEL}>
							Affiliate ID
						</label>
						<input
							id="pub-keys-affiliate"
							name="affiliateId"
							type="text"
							placeholder="aff_01"
							className={`${INPUT} sm:w-56`}
							value={props.affiliateId}
							onChange={(e) => props.setAffiliateId(e.currentTarget.value)}
						/>
					</div>
					<button type="submit" className={BTN_SECONDARY}>
						Load
					</button>
				</form>
			</header>
			<div className="overflow-x-auto rounded-[6px] border border-zinc-200">
				<table className="min-w-full divide-y divide-zinc-200 text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-[0.08em] text-zinc-500">
						<tr>
							<th scope="col" className="px-3 py-2 text-left">
								Key prefix
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Scopes
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Label
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Last used
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Status
							</th>
							<th scope="col" className="px-3 py-2 text-right">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{props.loading ? (
							<tr>
								<td colSpan={6} className="px-3 py-3 text-zinc-500">
									Loading keys.
								</td>
							</tr>
						) : sorted.length === 0 ? (
							<tr>
								<td colSpan={6} className="px-3 py-3 text-zinc-500">
									{props.error ??
										(props.affiliateId.trim().length === 0
											? "Enter an affiliate ID to load keys."
											: "No keys for this affiliate yet.")}
								</td>
							</tr>
						) : (
							sorted.map((row) => (
								<tr key={row.id}>
									<td className="px-3 py-2 font-mono text-xs text-zinc-700">
										{maskedKeyDisplay(row)}
									</td>
									<td className="px-3 py-2">
										<div className="flex flex-wrap gap-1">
											{row.scopes.length === 0 ? (
												<span className="text-xs text-zinc-400">—</span>
											) : (
												row.scopes.map((s) => (
													<span
														key={s}
														className="inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.06em] text-emerald-800"
													>
														{s}
													</span>
												))
											)}
										</div>
									</td>
									<td className="px-3 py-2 text-zinc-700">{row.label ?? "—"}</td>
									<td className="px-3 py-2 text-zinc-500">
										{formatLastUsed(row)}
									</td>
									<td className="px-3 py-2">
										<StatusBadge value={formatKeyStatus(row)} />
									</td>
									<td className="px-3 py-2 text-right">
										<button
											type="button"
											className={BTN_DANGER}
											onClick={async () => {
												await props.onRevoke(row.id);
											}}
											disabled={Boolean(row.revoked_at)}
											aria-label={`Revoke key ${row.key_prefix}`}
										>
											{row.revoked_at ? "Revoked" : "Revoke"}
										</button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			<p className={HINT}>
				Available scopes:
				{" "}
				{props.availableScopes.map((s) => (
					<span key={s} className="font-mono text-zinc-700">
						{" "}
						{s}
					</span>
				))}
			</p>
		</section>
	);
}

function StatusBadge(props: { value: "active" | "revoked" }) {
	if (props.value === "active") {
		return (
			<span className="inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
				Active
			</span>
		);
	}
	return (
		<span className="inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-600">
			Revoked
		</span>
	);
}

// ===========================================================================
// FS-226 console — publisher_id-scoped generator + key table + access log.
// Surfaces the newer canonical scope set (campaigns.list_approved, …) and
// the `key_preview` + `active` shape returned by `/publisher-api/v2/keys`.
// ===========================================================================

function PublisherV2Console() {
	const [publisherId, setPublisherId] = useState("");
	const [rows, setRows] = useState<PublisherApiKeyMaskedV2[]>([]);
	const [loadingRows, setLoadingRows] = useState(false);
	const [rowsError, setRowsError] = useState<string | null>(null);
	const [reveal, setReveal] =
		useState<PublisherApiKeyCreatedV2Response | null>(null);
	const [accessLog, setAccessLog] = useState<PublisherApiAccessLogEntry[]>([]);
	const [logLoading, setLogLoading] = useState(false);
	const [logLimit, setLogLimit] = useState(100);
	const [logError, setLogError] = useState<string | null>(null);

	const reloadKeys = useCallback(async (id: string) => {
		const trimmed = id.trim();
		if (!trimmed) {
			setRows([]);
			return;
		}
		setLoadingRows(true);
		setRowsError(null);
		try {
			const fresh = await fetchKeysV2(trimmed);
			setRows(fresh);
		} catch (err) {
			setRowsError(
				err instanceof Error ? err.message : "Unable to load keys.",
			);
		} finally {
			setLoadingRows(false);
		}
	}, []);

	const reloadAccessLog = useCallback(
		async (id: string, limit: number) => {
			const trimmed = id.trim();
			if (!trimmed) {
				setAccessLog([]);
				return;
			}
			setLogLoading(true);
			setLogError(null);
			try {
				const res = await fetchAccessLog(trimmed, limit);
				setAccessLog(res.entries);
			} catch (err) {
				setLogError(
					err instanceof Error ? err.message : "Unable to load access log.",
				);
			} finally {
				setLogLoading(false);
			}
		},
		[],
	);

	return (
		<section
			aria-label="Publisher API (v2) console"
			className={`${CARD} flex flex-col gap-6 p-6`}
			data-testid="publisher-api-v2-console"
		>
			<header className="flex flex-col gap-1">
				<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
					Partner API · v2
				</p>
				<h2 className="text-lg font-semibold text-zinc-900">
					Publisher-scoped key console
				</h2>
				<p className={HINT}>
					Manage keys by publisher ID. Newer endpoints return a 12-character
					<code className="mx-1 font-mono">key_preview</code> ("ABCD...WXYZ")
					in place of the legacy <code className="mx-1 font-mono">key_prefix</code>
					and surface a tenant+publisher-isolated access log.
				</p>
			</header>

			<V2GeneratorCard
				onGenerated={async (res, submittedId) => {
					setReveal(res);
					setPublisherId(submittedId);
					await reloadKeys(submittedId);
				}}
			/>

			<V2RevealPanel
				reveal={reveal}
				onDismiss={() => setReveal(null)}
			/>

			<V2KeysCard
				publisherId={publisherId}
				setPublisherId={setPublisherId}
				rows={rows}
				loading={loadingRows}
				error={rowsError}
				onReload={reloadKeys}
				onDelete={async (id) => {
					try {
						await deleteKeyV2(id);
						await reloadKeys(publisherId);
					} catch (err) {
						setRowsError(
							err instanceof Error
								? err.message
								: "Unable to revoke key.",
						);
					}
				}}
			/>

			<V2AccessLogCard
				publisherId={publisherId}
				entries={accessLog}
				loading={logLoading}
				error={logError}
				limit={logLimit}
				setLimit={setLogLimit}
				onReload={reloadAccessLog}
			/>
		</section>
	);
}

function V2GeneratorCard(props: {
	onGenerated: (
		res: PublisherApiKeyCreatedV2Response,
		publisherId: string,
	) => Promise<void>;
}) {
	const [publisherId, setPublisherId] = useState("");
	const [name, setName] = useState("");
	const [scopes, setScopes] = useState<PublisherScopeV2[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [serverError, setServerError] = useState<string | null>(null);

	function toggleScope(scope: PublisherScopeV2) {
		setScopes((prev) =>
			prev.includes(scope)
				? prev.filter((s) => s !== scope)
				: [...prev, scope],
		);
		setErrors((prev) => ({ ...prev, scopes: "" }));
	}

	async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = createKeyV2FormSchema.safeParse({
			publisherId,
			name,
			scopes,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			setServerError(null);
			return;
		}
		setSubmitting(true);
		setErrors({});
		setServerError(null);
		try {
			const res = await createKeyV2(parsed.data);
			await props.onGenerated(res, parsed.data.publisher_id);
			setName("");
			setScopes([]);
		} catch (err) {
			setServerError(
				err instanceof Error ? err.message : "Unable to generate key.",
			);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form
			onSubmit={onSubmit}
			className="flex flex-col gap-4 rounded-[6px] border border-zinc-200 bg-zinc-50 p-4"
			aria-label="V2 key generator"
			noValidate
		>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="pub-v2-id" className={LABEL}>
					Publisher ID
				</label>
				<input
					id="pub-v2-id"
					name="publisherId"
					type="text"
					placeholder="pub_01"
					className={INPUT}
					value={publisherId}
					onChange={(e) => {
						setPublisherId(e.currentTarget.value);
						setErrors((prev) => ({ ...prev, publisherId: "" }));
					}}
					aria-invalid={Boolean(errors.publisherId)}
					disabled={submitting}
				/>
				{errors.publisherId && (
					<p className={ERROR_TEXT}>{errors.publisherId}</p>
				)}
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="pub-v2-name" className={LABEL}>
					Key name
				</label>
				<input
					id="pub-v2-name"
					name="name"
					type="text"
					placeholder="server integration"
					className={INPUT}
					value={name}
					onChange={(e) => {
						setName(e.currentTarget.value);
						setErrors((prev) => ({ ...prev, name: "" }));
					}}
					aria-invalid={Boolean(errors.name)}
					disabled={submitting}
				/>
				{errors.name && <p className={ERROR_TEXT}>{errors.name}</p>}
			</div>
			<fieldset className="flex flex-col gap-2">
				<legend className={LABEL}>Scopes</legend>
				<div className="flex flex-wrap gap-2">
					{PUBLISHER_SCOPES_V2.map((scope) => {
						const checked = scopes.includes(scope);
						return (
							<label
								key={scope}
								className="inline-flex cursor-pointer items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-800 hover:border-sky-300 hover:bg-sky-50"
							>
								<input
									type="checkbox"
									name="v2Scope"
									value={scope}
									checked={checked}
									onChange={() => toggleScope(scope)}
									className="h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500"
									disabled={submitting}
								/>
								<span className="font-mono text-xs">{scope}</span>
							</label>
						);
					})}
				</div>
				{errors.scopes && <p className={ERROR_TEXT}>{errors.scopes}</p>}
			</fieldset>
			<div className="flex items-center justify-between gap-2">
				<p className={HINT}>Submits to POST /publisher-api/v2/keys</p>
				<button
					type="submit"
					className={BTN_PRIMARY}
					disabled={submitting}
				>
					{submitting ? "Generating." : "Generate v2 key"}
				</button>
			</div>
			{serverError && (
				<p className={ERROR_TEXT} role="alert">
					{serverError}
				</p>
			)}
		</form>
	);
}

function V2RevealPanel(props: {
	reveal: PublisherApiKeyCreatedV2Response | null;
	onDismiss: () => void;
}) {
	if (!props.reveal) return null;
	const { key, raw_key } = props.reveal;
	return (
		<section
			aria-label="One-time v2 key reveal"
			className="rounded-[8px] border border-sky-300 bg-sky-50 p-4"
		>
			<header className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-sky-800">
						Copy this key now
					</h3>
					<p className="text-xs text-sky-700">
						Preview {key.key_preview} · Publisher {key.publisher_id}
					</p>
				</div>
				<button
					type="button"
					className={BTN_SECONDARY}
					onClick={props.onDismiss}
				>
					Dismiss
				</button>
			</header>
			<code
				className="mt-3 block break-all rounded-[6px] border border-sky-400 bg-white px-3 py-2 font-mono text-sm text-sky-900"
				data-testid="publisher-api-v2-raw-key"
			>
				{raw_key}
			</code>
		</section>
	);
}

function V2KeysCard(props: {
	publisherId: string;
	setPublisherId: (id: string) => void;
	rows: PublisherApiKeyMaskedV2[];
	loading: boolean;
	error: string | null;
	onReload: (id: string) => Promise<void>;
	onDelete: (id: string) => Promise<void>;
}) {
	const sorted = useMemo(
		() =>
			[...props.rows].sort(
				(a, b) =>
					Number(!a.active) - Number(!b.active) ||
					b.created_at - a.created_at,
			),
		[props.rows],
	);
	return (
		<div className="flex flex-col gap-3 rounded-[6px] border border-zinc-200 bg-white p-4">
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h3 className="text-sm font-semibold text-zinc-900">Keys by publisher</h3>
					<p className={HINT}>
						Lists keys for the current publisher. Tenant-isolated on the API.
					</p>
				</div>
				<form
					className="flex items-end gap-2"
					onSubmit={async (e) => {
						e.preventDefault();
						await props.onReload(props.publisherId);
					}}
				>
					<div className="flex flex-col gap-1">
						<label htmlFor="pub-v2-lookup" className={LABEL}>
							Publisher ID
						</label>
						<input
							id="pub-v2-lookup"
							name="publisherId"
							type="text"
							placeholder="pub_01"
							className={`${INPUT} sm:w-56`}
							value={props.publisherId}
							onChange={(e) => props.setPublisherId(e.currentTarget.value)}
						/>
					</div>
					<button type="submit" className={BTN_SECONDARY}>
						Load
					</button>
				</form>
			</header>
			<div className="overflow-x-auto rounded-[6px] border border-zinc-200">
				<table className="min-w-full divide-y divide-zinc-200 text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-[0.08em] text-zinc-500">
						<tr>
							<th scope="col" className="px-3 py-2 text-left">
								Preview
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Name
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Scopes
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Status
							</th>
							<th scope="col" className="px-3 py-2 text-right">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{props.loading ? (
							<tr>
								<td colSpan={5} className="px-3 py-3 text-zinc-500">
									Loading keys.
								</td>
							</tr>
						) : sorted.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-3 py-3 text-zinc-500">
									{props.error ??
										(props.publisherId.trim().length === 0
											? "Enter a publisher ID to load keys."
											: "No keys for this publisher yet.")}
								</td>
							</tr>
						) : (
							sorted.map((row) => (
								<tr key={row.id}>
									<td className="px-3 py-2 font-mono text-xs text-zinc-700">
										{row.key_preview}
									</td>
									<td className="px-3 py-2 text-zinc-700">
										{row.name ?? "-"}
									</td>
									<td className="px-3 py-2">
										<div className="flex flex-wrap gap-1">
											{row.scopes.length === 0 ? (
												<span className="text-xs text-zinc-400">-</span>
											) : (
												row.scopes.map((s) => (
													<span
														key={s}
														className="inline-flex items-center rounded-[4px] border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.04em] text-sky-800"
													>
														{s}
													</span>
												))
											)}
										</div>
									</td>
									<td className="px-3 py-2">
										{row.active ? (
											<span className="inline-flex items-center rounded-[4px] border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
												Active
											</span>
										) : (
											<span className="inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-600">
												Revoked
											</span>
										)}
									</td>
									<td className="px-3 py-2 text-right">
										<button
											type="button"
											className={BTN_DANGER}
											onClick={async () => {
												await props.onDelete(row.id);
											}}
											disabled={!row.active}
											aria-label={`Revoke key ${row.key_preview}`}
										>
											{row.active ? "Revoke" : "Revoked"}
										</button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function V2AccessLogCard(props: {
	publisherId: string;
	entries: PublisherApiAccessLogEntry[];
	loading: boolean;
	error: string | null;
	limit: number;
	setLimit: (n: number) => void;
	onReload: (id: string, limit: number) => Promise<void>;
}) {
	return (
		<div className="flex flex-col gap-3 rounded-[6px] border border-zinc-200 bg-white p-4">
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h3 className="text-sm font-semibold text-zinc-900">
						Recent access log
					</h3>
					<p className={HINT}>
						Descending by timestamp, scoped to the publisher and tenant.
					</p>
				</div>
				<form
					className="flex items-end gap-2"
					onSubmit={async (e) => {
						e.preventDefault();
						await props.onReload(props.publisherId, props.limit);
					}}
				>
					<div className="flex flex-col gap-1">
						<label htmlFor="pub-v2-log-limit" className={LABEL}>
							Limit
						</label>
						<input
							id="pub-v2-log-limit"
							name="limit"
							type="number"
							min={1}
							max={500}
							className={`${INPUT} sm:w-28`}
							value={props.limit}
							onChange={(e) => {
								const n = Number(e.currentTarget.value);
								props.setLimit(Number.isFinite(n) ? n : 100);
							}}
						/>
					</div>
					<button type="submit" className={BTN_SECONDARY}>
						Load log
					</button>
				</form>
			</header>
			<div className="overflow-x-auto rounded-[6px] border border-zinc-200">
				<table className="min-w-full divide-y divide-zinc-200 text-xs">
					<thead className="bg-zinc-50 uppercase tracking-[0.08em] text-zinc-500">
						<tr>
							<th scope="col" className="px-3 py-2 text-left">
								When
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Method
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Path
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Status
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Key
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{props.loading ? (
							<tr>
								<td colSpan={5} className="px-3 py-3 text-zinc-500">
									Loading.
								</td>
							</tr>
						) : props.entries.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-3 py-3 text-zinc-500">
									{props.error ??
										(props.publisherId.trim().length === 0
											? "Enter a publisher ID to load the log."
											: "No log entries yet.")}
								</td>
							</tr>
						) : (
							props.entries.map((e) => (
								<tr key={e.id}>
									<td className="px-3 py-2 text-zinc-700">
										{formatAccessLogTimestamp(e.ts)}
									</td>
									<td className="px-3 py-2 font-mono text-zinc-700">
										{e.method}
									</td>
									<td className="px-3 py-2 font-mono text-zinc-700">
										{e.path}
									</td>
									<td className="px-3 py-2 text-zinc-700">{e.status_code}</td>
									<td className="px-3 py-2 font-mono text-zinc-500">
										{e.key_id}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function EndpointsCatalog() {
	const [endpoints, setEndpoints] = useState<PublisherApiEndpointDescriptor[]>(
		[],
	);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await fetchEndpoints();
				if (!cancelled) {
					setEndpoints(res.endpoints);
					setLoaded(true);
				}
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error
							? err.message
							: "Unable to load endpoint catalog.",
					);
					setLoaded(true);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<section
			aria-label="Partner API endpoint catalog"
			className={`${CARD} flex flex-col gap-3 p-6`}
			data-testid="publisher-api-endpoints"
		>
			<header className="flex flex-col gap-1">
				<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
					Partner API · catalog
				</p>
				<h2 className="text-lg font-semibold text-zinc-900">
					Publisher-facing endpoints
				</h2>
				<p className={HINT}>
					Static descriptor of the partner surface. Each entry lists the
					required scope so integrations can choose the minimum set.
				</p>
			</header>
			<ul className="flex flex-col gap-2" data-testid="publisher-api-endpoints-list">
				{!loaded ? (
					<li className="text-sm text-zinc-500">Loading endpoints.</li>
				) : error ? (
					<li className="text-sm text-red-600">{error}</li>
				) : (
					endpoints.map((ep) => (
						<li
							key={ep.path}
							className="flex flex-col gap-0.5 rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2"
						>
							<code className="font-mono text-xs text-zinc-800">
								{ep.method} {ep.path}
							</code>
							<span className="text-xs text-zinc-600">{ep.description}</span>
							<span className="text-[11px] font-mono uppercase tracking-[0.06em] text-sky-700">
								scope: {ep.required_scope}
							</span>
						</li>
					))
				)}
			</ul>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Footer hint
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">Partner endpoints</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					<code className="font-mono text-zinc-800">GET /api/partner/v1/campaigns</code>
					{" "}— requires the <code className="font-mono">campaigns</code> scope.
				</li>
				<li>
					<code className="font-mono text-zinc-800">GET /api/partner/v1/stats</code>
					{" "}— requires the <code className="font-mono">stats</code> scope;
					accepts <code className="font-mono">from</code> and
					{" "}<code className="font-mono">to</code> UNIX-second query params.
				</li>
				<li>
					Authenticate with{" "}
					<code className="font-mono">x-publisher-api-key:&nbsp;PREFIX.secret</code>.
				</li>
			</ul>
		</aside>
	);
}
